import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FilterContext";

const DOT = { success: "bg-emerald-500", partial: "bg-amber-500", failed: "bg-rose-500", running: "bg-sky-500 animate-pulse" };
const LABEL = { success: "Berhasil", partial: "Sebagian", failed: "Gagal", running: "Berjalan" };

export const SyncIndicator = () => {
  const { user } = useAuth();
  const { refreshData } = useFilters();
  const [st, setSt] = useState(null);
  const timer = useRef(null);

  const load = useCallback(() => api.get("/sync/status").then((r) => { setSt(r.data); return r.data; }), []);

  const poll = useCallback(() => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      const d = await load();
      if (!d.running && d.last?.status !== "running") {
        clearInterval(timer.current);
        const s = d.last?.status;
        if (s === "failed") toast.error(`Sinkronisasi gagal: ${d.last?.message || "lihat log"}`);
        else toast.success(`Sinkronisasi ${LABEL[s]?.toLowerCase()} — ${d.last?.total_records} baris, ${d.last?.warnings} peringatan`);
        refreshData();
      }
    }, 2000);
  }, [load, refreshData]);

  useEffect(() => { load(); return () => clearInterval(timer.current); }, [load]);

  const run = async () => {
    try {
      await api.post("/sync/run");
      setSt((s) => ({ ...s, running: true, last: { ...(s?.last || {}), status: "running" } }));
      poll();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const last = st?.last;
  const status = st?.running ? "running" : last?.status;
  return (
    <div className="flex items-center gap-2">
      <div data-testid="sync-status-indicator" className="hidden md:flex items-center gap-2 rounded-full border px-3 py-1 text-xs">
        <span className={`h-2 w-2 rounded-full ${DOT[status] || "bg-slate-400"}`} />
        <span className="text-muted-foreground">Sync:</span>
        <span className="font-medium">{LABEL[status] || "–"}</span>
        <span className="text-muted-foreground">· {fmtDate(last?.finished_at || last?.started_at)}</span>
      </div>
      {user?.role === "admin" && (
        <Button data-testid="refresh-sync-button" size="sm" onClick={run} disabled={status === "running"} className="h-8">
          {status === "running" ? <Loader2 className="h-4 w-4 animate-spin sm:mr-1.5" /> : <RefreshCw className="h-4 w-4 sm:mr-1.5" />}
          <span className="hidden sm:inline">Refresh Sekarang</span>
        </Button>
      )}
    </div>
  );
};
